import { useState, useEffect, useRef } from "react"
import { Input } from "./input"

/** Format a number as German currency: comma for decimals, period as thousand separator (e.g. 1234.56 → "1.234,56") */
export function formatGermanCurrency(value: number): string {
  const fixed = value.toFixed(2)
  const [intPart, decPart] = fixed.split(".")
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
  return withThousands + "," + decPart
}

/** Parse German-style input: strip thousand separators (.), then treat comma as decimal */
function parseGermanNumber(input: string): number {
  const withoutThousands = input.replace(/\./g, "")
  const normalized = withoutThousands.replace(",", ".")
  return Number(normalized) || 0
}

export function PriceInput({
  value,
  onChange,
  onBlur,
  id,
  name,
  className,
  placeholder = "z. B. 1.234,56",
  disabled = false,
  ...props
}: {
  value: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  id?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
} & Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "onBlur" | "type" | "inputMode">) {
  const [displayValue, setDisplayValue] = useState<string>("")
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync display value when external value changes, but only if not focused
  // value is in cents, so divide by 100 for display; format German-style (e.g. 1.234,56)
  useEffect(() => {
    if (!isFocused) {
      if (value != null && value !== 0) {
        const euroValue = value / 100
        setDisplayValue(formatGermanCurrency(euroValue))
      } else if (value === 0) {
        setDisplayValue("0")
      } else {
        setDisplayValue("")
      }
    }
  }, [value, isFocused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value

    // Allow: digits, period as thousand separator, one comma, max 2 decimal places
    const regex = /^[\d.]*(,\d{0,2})?$/

    if (regex.test(v) || v === "") {
      setDisplayValue(v)

      if (v === "" || v === ",") {
        onChange(0)
      } else {
        const euroValue = parseGermanNumber(v)
        const centsValue = Math.max(0, Math.round(euroValue * 100))
        onChange(centsValue)
      }
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)

    const inputValue = e.target.value
    if (inputValue === "" || inputValue === ",") {
      onChange(0)
      setDisplayValue("0")
    } else {
      let toParse = inputValue
      if (toParse.endsWith(",")) {
        toParse = toParse + "00"
      }

      const euroValue = Math.max(0, parseGermanNumber(toParse))
      const centsValue = Math.round(euroValue * 100)
      onChange(centsValue)

      if (centsValue !== 0) {
        setDisplayValue(formatGermanCurrency(euroValue))
      } else {
        setDisplayValue("0")
      }
    }
    onBlur?.()
  }

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      disabled={disabled}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      id={id}
      name={name}
      className={className}
    />
  )
}